import React, { useEffect } from "react";
import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AlertCircle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useCreateNotification } from "@/features/myEmailNotifications/hooks/useCreateNotification";

const CreateNotificationForm: React.FC = () => {
  const {
    form,
    templates,
    previewHtml,
    isLoading,
    error,
    createLoading,
    createError,
    handlePreview,
    submitForm,
    handleCancel,
  } = useCreateNotification();

  const { handleSubmit, control, watch } = form;
  const mailchimpTemplateId = watch("mailchimpTemplateId");

  return (
    <MainPageWrapper title="Create Email Notification">
      <div className="mb-6">
        <p className="text-gray-600">
          Create an email notification to send to your chapter members. All
          notifications require approval before being sent. Fields marked with *
          are required.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Form Section */}
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <Form {...form}>
            <form className="space-y-6" onSubmit={handleSubmit(submitForm)}>
              {/* Email Subject */}
              <FormField
                control={control}
                name="subject"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email Subject *</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Enter email subject" />
                    </FormControl>
                    <FormDescription>
                      The subject line that will appear in recipients' inboxes
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Email Template Selection */}
              <FormField
                control={control}
                name="mailchimpTemplateId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email Template *</FormLabel>
                    <FormControl>
                      <Select
                        disabled={isLoading || templates.length === 0}
                        onValueChange={(value) => field.onChange(Number(value))}
                        value={field.value?.toString() || ""}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select a template" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectGroup>
                            {templates.map((template) => (
                              <SelectItem
                                key={template.id}
                                value={template.id.toString()}
                              >
                                {template.templateName}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormDescription>
                      Choose a template that will determine the appearance of
                      your email
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Email Content */}
              <FormField
                control={control}
                name="customContent"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email Content *</FormLabel>
                    <FormControl>
                      <Textarea
                        {...field}
                        className="min-h-[200px]"
                        placeholder="Enter your message to chapter members here..."
                      />
                    </FormControl>
                    <FormDescription>
                      This text will be inserted into the template you selected
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Error messages */}
              {(error || createError) && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error || createError}</AlertDescription>
                </Alert>
              )}

              {/* Form actions */}
              <div className="flex justify-between items-center pt-4">
                <Button
                  disabled={isLoading || !mailchimpTemplateId}
                  onClick={handlePreview}
                  type="button"
                  variant="outline"
                >
                  {isLoading ? "Loading..." : "Preview Email"}
                </Button>

                <div className="flex gap-4">
                  <Button
                    disabled={createLoading}
                    onClick={handleCancel}
                    type="button"
                    variant="outline"
                  >
                    Cancel
                  </Button>
                  <Button disabled={isLoading || createLoading} type="submit">
                    {createLoading ? "Submitting..." : "Submit for Approval"}
                  </Button>
                </div>
              </div>
            </form>
          </Form>
        </div>

        {/* Preview Section */}
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden flex flex-col">
          <div className="bg-gray-50 p-4 border-b">
            <h3 className="font-medium text-gray-900">Email Preview</h3>
            <p className="text-sm text-gray-500">
              This is how your email will appear to recipients
            </p>
          </div>

          <div className="p-4 flex-grow overflow-auto">
            {previewHtml ? (
              <div
                className="email-preview"
                dangerouslySetInnerHTML={{ __html: previewHtml }}
              />
            ) : (
              <div className="flex h-full items-center justify-center text-gray-400 text-center p-8">
                <p>
                  Select a template and click "Preview Email" to see how your
                  notification will look.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </MainPageWrapper>
  );
};

export default CreateNotificationForm;
